// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// Company Management Component - CSR assignment and analytics management
// Date: July 26, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';

export interface CompanyWithAssignment {
  id: string;
  name: string;
  domain?: string;
  assigned_csr_id?: string;
  assigned_csr_email?: string;
  dashboard_url?: string;
  campaign_count: number;
  user_count: number;
  created_at: string;
  updated_at?: string;
}

interface CSRUser {
  id: string;
  email: string;
  full_name?: string;
}

const AdminCompanyManagement: React.FC = () => {
  console.log('🏗️ AdminCompanyManagement: Component initializing...');
  const [companies, setCompanies] = useState<CompanyWithAssignment[]>([]);
  const [csrUsers, setCsrUsers] = useState<CSRUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [processingCompanyId, setProcessingCompanyId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof CompanyWithAssignment | null;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });

  // Fetch companies with assignment and analytics data
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          domain,
          assigned_csr_id,
          created_at,
          updated_at
        `)
        .order('name'); // Default sorting - will be overridden by frontend sorting

      if (companiesError) throw companiesError;

      // Get CSR emails for assigned companies
      const companyCSRIds = companiesData
        ?.filter(c => c.assigned_csr_id)
        .map(c => c.assigned_csr_id) || [];

      let csrEmails: { [key: string]: string } = {};
      if (companyCSRIds.length > 0) {
        const { data: csrData, error: csrError } = await supabase
          .from('users')
          .select('id, email')
          .in('id', companyCSRIds);

        if (!csrError && csrData) {
          csrEmails = csrData.reduce((acc, csr) => {
            acc[csr.id] = csr.email;
            return acc;
          }, {} as { [key: string]: string });
        }
      }

      // Get analytics URLs for companies
      const { data: analyticsData, error: analyticsError } = await supabase
        .from('company_analytics')
        .select('company_id, dashboard_url');

      if (analyticsError) {
        console.warn('Could not fetch analytics data:', analyticsError);
      }

      const analyticsMap = analyticsData?.reduce((acc, item) => {
        acc[item.company_id] = item.dashboard_url;
        return acc;
      }, {} as { [key: string]: string }) || {};

      // Get user counts for each company
      const { data: userCounts, error: userCountError } = await supabase
        .from('users')
        .select('company_id')
        .not('company_id', 'is', null);

      if (userCountError) {
        console.warn('Could not fetch user counts:', userCountError);
      }

      const userCountMap = userCounts?.reduce((acc, user) => {
        acc[user.company_id] = (acc[user.company_id] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number }) || {};

      // Get campaign counts for each company
      // First get all campaigns
      const { data: allCampaigns, error: campaignError } = await supabase
        .from('campaigns')
        .select('*');

      if (campaignError) {
        console.error('Could not fetch campaigns:', campaignError);
      }

      // Get user to company mapping
      const campaignCountMap: { [key: string]: number } = {};
      
      if (allCampaigns && allCampaigns.length > 0) {
        // Get unique user IDs from campaigns
        const userIds = [...new Set(allCampaigns.map(c => c.user_id))];
        
        // Fetch user company mappings separately (avoids the join issue)
        const { data: userMappings, error: userError } = await supabase
          .from('users')
          .select('id, company_id')
          .in('id', userIds);
        
        if (userError) {
          console.error('Could not fetch user mappings:', userError);
        }
        
        // Create user to company map
        const userToCompanyMap: { [key: string]: string } = {};
        userMappings?.forEach(user => {
          if (user.company_id) {
            userToCompanyMap[user.id] = user.company_id;
          }
        });
        
        // Count campaigns per company
        allCampaigns.forEach(campaign => {
          const companyId = userToCompanyMap[campaign.user_id];
          if (companyId) {
            campaignCountMap[companyId] = (campaignCountMap[companyId] || 0) + 1;
          }
        });
      }
      
      const totalCampaigns = Object.values(campaignCountMap).reduce((sum, count) => sum + count, 0);
      console.log(`📊 Found ${totalCampaigns} campaigns across ${Object.keys(campaignCountMap).length} companies`);

      // Transform data for display
      const transformedCompanies: CompanyWithAssignment[] = companiesData?.map(company => ({
        id: company.id,
        name: company.name,
        domain: company.domain,
        assigned_csr_id: company.assigned_csr_id,
        assigned_csr_email: company.assigned_csr_id ? csrEmails[company.assigned_csr_id] : undefined,
        dashboard_url: analyticsMap[company.id],
        user_count: userCountMap[company.id] || 0,
        campaign_count: campaignCountMap[company.id] || 0,
        created_at: company.created_at,
        updated_at: company.updated_at
      })) || [];

      setCompanies(transformedCompanies);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch available CSR users
  const fetchCSRUsers = useCallback(async () => {
    try {
      console.log('🔍 Fetching CSR users...');
      
      // Try RPC function first (like useAdminCampaigns does)
      let data = null;
      let error = null;
      
      try {
        const rpcResult = await supabase.rpc('get_users_by_role', { user_role: 'csr' });
        data = rpcResult.data;
        error = rpcResult.error;
        console.log('✅ RPC CSR users fetch successful:', data);
      } catch (rpcErr) {
        console.log('⚠️ RPC function not available, trying direct query:', rpcErr);
        
        // Fallback to direct query
        const directResult = await supabase
          .from('users')
          .select('id, email, full_name')
          .eq('role', 'csr')
          .order('email');
        
        data = directResult.data;
        error = directResult.error;
        console.log('✅ Direct CSR users fetch result:', data);
      }

      if (error) {
        console.error('❌ CSR users query error:', error);
        throw error;
      }
      
      console.log('✅ CSR users fetched:', data);
      setCsrUsers(data || []);
    } catch (err) {
      console.error('❌ Failed to fetch CSR users:', err);
    }
  }, []);

  // Handle table sorting
  const handleSort = (key: keyof CompanyWithAssignment) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort companies based on current sort configuration
  const sortedCompanies = React.useMemo(() => {
    if (!sortConfig.key) return companies;

    return [...companies].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];
      
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      // For dates
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(aValue as string).getTime();
        const dateB = new Date(bValue as string).getTime();
        const result = dateA - dateB;
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      return 0;
    });
  }, [companies, sortConfig]);

  // Get sort indicator icon
  const getSortIcon = (columnKey: keyof CompanyWithAssignment) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Handle CSR assignment to company
  const handleCSRAssignment = async (companyId: string, csrId: string | null) => {
    setProcessingCompanyId(companyId);
    setError('');

    try {
      // Update company CSR assignment
      const { error: companyError } = await supabase
        .from('companies')
        .update({ 
          assigned_csr_id: csrId,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);

      if (companyError) throw companyError;

      // The database trigger will automatically handle campaign assignments
      // Refresh companies data to reflect changes
      await fetchCompanies();
      
      const company = companies.find(c => c.id === companyId);
      const csrEmail = csrId ? csrUsers.find(csr => csr.id === csrId)?.email : null;
      
      if (csrId && csrEmail) {
        console.log(`✅ CSR ${csrEmail} assigned to company ${company?.name}. All company campaigns auto-assigned.`);
      } else {
        console.log(`✅ CSR removed from company ${company?.name}. Company campaigns unassigned.`);
      }

    } catch (err) {
      console.error('CSR assignment failed:', err);
      setError(`Failed to assign CSR: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setProcessingCompanyId(null);
    }
  };

  // Handle analytics URL update
  const handleAnalyticsURLUpdate = async (companyId: string, dashboardUrl: string) => {
    setProcessingCompanyId(companyId);
    setError('');

    try {
      // First check if an analytics record exists for this company
      const { data: existing, error: fetchError } = await supabase
        .from('company_analytics')
        .select('id')
        .eq('company_id', companyId)
        .single();

      // Handle both "no rows found" and RLS errors gracefully
      // 406 = RLS policy violation (which we'll treat as "no existing record")
      // PGRST116 = no rows found
      if (fetchError && fetchError.code !== 'PGRST116' && !fetchError.message?.includes('406')) {
        console.log('Analytics check error (treating as no existing record):', fetchError);
        // Don't throw here, just assume no existing record
      }

      let error;
      
      if (existing) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('company_analytics')
          .update({ 
            dashboard_url: dashboardUrl.trim(),
            updated_at: new Date().toISOString()
          })
          .eq('company_id', companyId);
        error = updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('company_analytics')
          .insert({ 
            company_id: companyId, 
            dashboard_url: dashboardUrl.trim(),
            mainbase_company_id: `company_${companyId}`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        error = insertError;
      }

      if (error) throw error;

      // Refresh companies data
      await fetchCompanies();
      
      const company = companies.find(c => c.id === companyId);
      console.log(`✅ Analytics URL updated for company ${company?.name}`);

    } catch (err) {
      console.error('Analytics URL update failed:', err);
      setError(`Failed to update analytics URL: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setProcessingCompanyId(null);
    }
  };

  // Load data on component mount
  useEffect(() => {
    console.log('🚀 AdminCompanyManagement: Component mounted, loading data...');
    fetchCompanies();
    fetchCSRUsers();
  }, [fetchCompanies, fetchCSRUsers]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Company Management</h2>
          <p className="text-gray-600">
            Assign CSRs to companies and manage analytics URLs. Company assignments auto-assign all campaigns.
          </p>
        </div>
        <button
          onClick={fetchCompanies}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh Data
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Company Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-blue-600">{companies.length}</div>
          <div className="text-sm text-gray-600">Total Companies</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-green-600">
            {companies.filter(c => c.assigned_csr_id).length}
          </div>
          <div className="text-sm text-gray-600">Companies with CSR</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-purple-600">
            {companies.reduce((sum, c) => sum + c.campaign_count, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Campaigns</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-orange-600">
            {companies.filter(c => c.dashboard_url).length}
          </div>
          <div className="text-sm text-gray-600">Analytics Configured</div>
        </div>
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Company
                  {getSortIcon('name')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('user_count')}
              >
                <div className="flex items-center">
                  Users / Campaigns
                  {getSortIcon('user_count')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('assigned_csr_email')}
              >
                <div className="flex items-center">
                  Assigned CSR
                  {getSortIcon('assigned_csr_email')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Analytics URL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedCompanies.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No companies found
                </td>
              </tr>
            ) : (
              sortedCompanies.map((company) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  csrUsers={csrUsers}
                  onCSRAssignment={handleCSRAssignment}
                  onAnalyticsUpdate={handleAnalyticsURLUpdate}
                  isProcessing={processingCompanyId === company.id}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Information */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-2">How Company Assignment Works:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Assigning a CSR to a company automatically assigns ALL campaigns from users in that company</li>
          <li>• Company assignments override individual campaign assignments</li>
          <li>• New campaigns from company users are automatically assigned to the company CSR</li>
          <li>• Company assignments have 5x workload weight vs 3x for individual campaign assignments</li>
          <li>• Removing a CSR from a company unassigns all company campaigns</li>
        </ul>
      </div>
    </div>
  );
};

// Individual company row component
interface CompanyRowProps {
  company: CompanyWithAssignment;
  csrUsers: CSRUser[];
  onCSRAssignment: (companyId: string, csrId: string | null) => Promise<void>;
  onAnalyticsUpdate: (companyId: string, url: string) => Promise<void>;
  isProcessing: boolean;
}

const CompanyRow: React.FC<CompanyRowProps> = ({
  company,
  csrUsers,
  onCSRAssignment,
  onAnalyticsUpdate,
  isProcessing
}) => {
  const [analyticsUrl, setAnalyticsUrl] = useState(company.dashboard_url || '');
  const [showAnalyticsInput, setShowAnalyticsInput] = useState(false);

  const handleCSRChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const csrId = e.target.value || null;
    onCSRAssignment(company.id, csrId);
  };

  const handleAnalyticsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (analyticsUrl.trim() !== (company.dashboard_url || '')) {
      onAnalyticsUpdate(company.id, analyticsUrl);
      setShowAnalyticsInput(false);
    }
  };

  const handleAnalyticsCancel = () => {
    setAnalyticsUrl(company.dashboard_url || '');
    setShowAnalyticsInput(false);
  };

  return (
    <tr className={`hover:bg-gray-50 ${isProcessing ? 'opacity-50' : ''}`}>
      {/* Company Name */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{company.name}</div>
          {company.domain && (
            <div className="text-sm text-gray-500">{company.domain}</div>
          )}
        </div>
      </td>

      {/* Users / Campaigns */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {company.user_count} users • {company.campaign_count} campaigns
        </div>
        <div className="text-xs text-gray-500">
          Created: {new Date(company.created_at).toLocaleDateString()}
        </div>
      </td>

      {/* Assigned CSR */}
      <td className="px-6 py-4 whitespace-nowrap">
        <select
          value={company.assigned_csr_id || ''}
          onChange={handleCSRChange}
          disabled={isProcessing}
          className="text-sm border border-gray-300 rounded px-2 py-1 w-full max-w-xs"
        >
          <option value="">No CSR Assigned</option>
          {csrUsers.map((csr) => (
            <option key={csr.id} value={csr.id}>
              {csr.email}
            </option>
          ))}
        </select>
        {company.assigned_csr_email && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              🏢 Company CSR
            </span>
          </div>
        )}
      </td>

      {/* Analytics URL */}
      <td className="px-6 py-4">
        {showAnalyticsInput ? (
          <form onSubmit={handleAnalyticsSubmit} className="space-y-2">
            <input
              type="url"
              value={analyticsUrl}
              onChange={(e) => setAnalyticsUrl(e.target.value)}
              placeholder="https://example.com/dashboard"
              className="text-sm border border-gray-300 rounded px-2 py-1 w-full"
              disabled={isProcessing}
            />
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={isProcessing}
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleAnalyticsCancel}
                disabled={isProcessing}
                className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-1">
            {company.dashboard_url ? (
              <div className="text-sm text-blue-600 truncate max-w-xs">
                <a
                  href={company.dashboard_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {company.dashboard_url}
                </a>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No analytics URL</div>
            )}
            <button
              onClick={() => setShowAnalyticsInput(true)}
              disabled={isProcessing}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              {company.dashboard_url ? 'Edit URL' : 'Add URL'}
            </button>
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {isProcessing ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2">Processing...</span>
          </div>
        ) : (
          <div className="flex space-x-2">
            {company.assigned_csr_email && (
              <span className="text-xs text-green-600">
                Auto-assigned
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

export default AdminCompanyManagement;